import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link as LinkIcon, Image as ImageIcon, Undo, Redo,
  AlignLeft, AlignCenter, AlignRight, Palette,
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolbarBtn = ({ onClick, active, disabled, children, title, testid }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`p-2 rounded transition-colors ${
      active
        ? 'bg-amber-500/20 text-amber-400'
        : 'text-slate-300 hover:bg-slate-800 hover:text-slate-100'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-6 bg-slate-700 mx-1" />;

const RichEditor = ({ value, onChange, placeholder = 'Start writing...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-amber-400 underline' } }),
      Image.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg my-4 max-w-full h-auto' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.({
        html: editor.getHTML(),
        json: editor.getJSON(),
      });
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-4 py-3 prose-headings:text-slate-100 prose-p:text-slate-300 prose-strong:text-slate-100 prose-a:text-amber-400 prose-blockquote:text-slate-400 prose-blockquote:border-amber-500/50',
        'data-testid': 'rich-editor-content',
      },
    },
  });

  // Re-load content if value changes externally (e.g., when fetching an article to edit)
  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL (leave empty to remove):', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('adminToken');
      const { data } = await axios.post(`${API}/admin/knowledge/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      editor.chain().focus().setImage({ src: data.url }).run();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const setColor = () => {
    const color = window.prompt('Hex color (e.g. #f59e0b):', editor.getAttributes('textStyle').color || '#f59e0b');
    if (color === null) return;
    if (color === '') editor.chain().focus().unsetColor().run();
    else editor.chain().focus().setColor(color).run();
  };

  if (!editor) return null;

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden bg-slate-900/50" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-slate-700 bg-slate-900 sticky top-0 z-10">
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Cmd+Z)" testid="editor-undo"><Undo className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Cmd+Shift+Z)" testid="editor-redo"><Redo className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1" testid="editor-h1"><Heading1 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2" testid="editor-h2"><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3" testid="editor-h3"><Heading3 className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Cmd+B)" testid="editor-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Cmd+I)" testid="editor-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Cmd+U)" testid="editor-underline"><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testid="editor-strike"><Strikethrough className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={setColor} active={!!editor.getAttributes('textStyle').color} title="Text color" testid="editor-color"><Palette className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list" testid="editor-ul"><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list" testid="editor-ol"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote" testid="editor-quote"><Quote className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block" testid="editor-code"><Code className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left" testid="editor-align-left"><AlignLeft className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center" testid="editor-align-center"><AlignCenter className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right" testid="editor-align-right"><AlignRight className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Add link" testid="editor-link"><LinkIcon className="w-4 h-4" /></ToolbarBtn>
        <label className="p-2 rounded text-slate-300 hover:bg-slate-800 hover:text-slate-100 cursor-pointer" title="Insert image">
          <ImageIcon className="w-4 h-4" />
          <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={uploadImage} className="hidden" data-testid="editor-image-input" />
        </label>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichEditor;
